import { API } from 'common';
import Loader from 'components/Loader';
import Page from 'components/Page';
import { toastRef } from 'components/Toast';
import { get, set, sumBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from "react-router-dom";
import { Button, Input } from 'reactstrap';
import { formatCurrency } from 'utils/formatCurrency';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import { v4 as uuid } from 'uuid';
import ModalConfirm, { ModalConfirmHandle } from 'widgets/ModalConfirm';
import ModalReason, { ModalReasonHandle } from 'widgets/ModalReason';
import ServicePicker, { ServicePickerHandle } from 'widgets/ServicePicker';
import { StaffBSPicker, StaffBSPickerHandle } from 'widgets/StafFilterInput';
import TimeSlotPicker, { TimeSlotPickerHandle } from 'widgets/TimeSlotPicker';
import anyoneIcon from './Icon/anyone.svg';
import closeIcon from './Icon/close.svg';
import editIcon from './Icon/edit.svg';
import downIcon from './Icon/down.svg';
import AssignAppointment from 'components/Modal/AssignAppointment';
import { useDispatch, useSelector } from 'react-redux';
import aptSelectors from 'store/appt/selectors';
import shopSelectors from 'store/shop/selectors';
import shopActions from 'store/shop/actions';

export const AppointmentStatus = {
  NEW: '0',
  CONFIRMED: '1',
  CHECK_IN: '2',
  FINISH: '3',
  CANCEL: '4',
  DELETED: '5',
}

// Service line as persisted/edited by this screen; distinct from widgets/ServicePicker's
// `Service` model (which has no `staffId`) since this view tracks per-service staff assignment.
interface AppointmentServiceItem {
  id?: number | string;
  serviceName: string;
  duration: number;
  price: number;
  staffId?: string | number;
  uuid: string;
  startTime?: string;
  endTime?: string;
}

// GET appointment/getAppointmentById payload — a different shape than
// types/models.ts's `AppointmentDetail` (which models admin/appointments-detail).
interface DetailAppointmentData {
  status?: string;
  duration?: number;
  customerName?: string;
  customerPhone?: string;
  note?: string;
  staffId?: string | number;
  name?: string;
  avatar?: string;
  strStartDate?: string;
  services?: AppointmentServiceItem[];
  shop?: { apiKey?: string };
}

interface SelectedStaff {
  id: string;
  name?: string;
  avatar?: string;
}

interface DetailAppointmentV2Props {
  staffUsed?: boolean;
}

const DetailAppointmentV2 = ({ staffUsed }: DetailAppointmentV2Props) => {
  const _history = useHistory<{ disabled?: boolean }>();
  const dispatch = useDispatch();
  const modalConfirm = React.useRef<ModalConfirmHandle>(null);
  const staffPicker = useRef<StaffBSPickerHandle>(null);
  const timePicker = useRef<TimeSlotPickerHandle>(null);
  const servicePicker = useRef<ServicePickerHandle>(null);
  const reasonRef = useRef<ModalReasonHandle>(null);
  const goBack = () => staffUsed ? _history.goBack() : _history.push('/appointments');
  const [isOpenAssign, setIsOpenAssign] = useState(false);

  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<DetailAppointmentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [note, setNote] = useState('');
  const readOnly = staffUsed || detail?.status === AppointmentStatus.CANCEL;
  const [services, setServices] = useState<AppointmentServiceItem[]>([]);
  const [staff, setStaff] = useState<SelectedStaff | null>(null);
  const [strStartDate, setStrStartDate] = useState('');

  const _staffList = useSelector(shopSelectors.staff);

  const get_detail_appointment = async (hideLoading?: boolean) => {
    if (!hideLoading)
      setLoadingPage(true);
    try {
      let response = await API.get(`appointment/getAppointmentById/${id}`);
      if (response.status == 200) {
        let { payload } = response.data;
        setDetail(payload || null);
        setNote(payload.note || '');
        const startTimeTemp = moment(payload.strStartDate, 'MM-DD-YYYY hh:mm A');
        setServices((payload.services || []).map((o: any) => ({
          ...o,
          staffId: payload.staffId || '',
          uuid: uuid(),
          startTime: startTimeTemp?.format('MM-DD-YYYY HH:mm') || '',
          endTime: startTimeTemp?.add(o.duration, 'minute').format('MM-DD-YYYY HH:mm') || '',
        })));
        setStaff({ id: payload.staffId, name: payload.name, avatar: payload.avatar });
        setStrStartDate(payload.strStartDate || '');
      }
    } catch (error) {
      console.error('get_detail_appointment');
    } finally {
      if (!hideLoading)
        setLoadingPage(false);
    }
  }
  // Coming from the calendar, the tapped appointment is already in the appt
  // store — seed the page from that cache and refresh silently (no blocker).
  // Prices are absent from calendar data, so they start at 0 until the fetch lands.
  const cachedCalendarApt = useSelector(aptSelectors.appointments).find(a => String(a.data?.id) === String(id))?.data;

  useEffect(() => {
    if (!cachedCalendarApt) {
      get_detail_appointment();
      return;
    }
    const startMoment = moment(cachedCalendarApt.startTime, 'MM-DD-YYYY HH:mm:ss');
    const strStart = startMoment.isValid() ? startMoment.format('MM-DD-YYYY hh:mm A') : '';
    const staffOption = _staffList.find(s => String(s.id) === String(cachedCalendarApt.staffId));
    setDetail({
      status: cachedCalendarApt.status,
      customerName: cachedCalendarApt.customerName,
      customerPhone: cachedCalendarApt.customerPhone,
      note: cachedCalendarApt.note,
      staffId: cachedCalendarApt.staffId,
      name: staffOption?.name,
      avatar: staffOption?.avatar,
      strStartDate: strStart,
      services: [],
    });
    setNote(cachedCalendarApt.note || '');
    const seedTime = startMoment.clone();
    setServices((cachedCalendarApt.services || []).map(o => ({
      id: o.serviceId,
      serviceName: o.serviceName,
      duration: o.duration,
      price: 0,
      staffId: cachedCalendarApt.staffId || '',
      uuid: uuid(),
      startTime: startMoment.isValid() ? seedTime.format('MM-DD-YYYY HH:mm') : '',
      endTime: startMoment.isValid() ? seedTime.add(o.duration, 'minute').format('MM-DD-YYYY HH:mm') : '',
    })));
    if (cachedCalendarApt.staffId) {
      setStaff({ id: cachedCalendarApt.staffId, name: staffOption?.name, avatar: staffOption?.avatar });
    }
    setStrStartDate(strStart);
    setLoadingPage(false);
    get_detail_appointment(true); // silent background refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeStaff = () => {
    if (!staffPicker.current) return;
    // `_staff.staffId` is undefined here (StaffOption has no `staffId` field) — kept
    // as-is to match existing runtime behavior; loosely typed to avoid fighting it.
    // `staff` is set alongside `detail` in get_detail_appointment; this handler is only
    // reachable once the `!detail` early-return has passed, so `staff` is non-null here.
    staffPicker.current.open(staff!.id, (_staff: any) => {
      setStaff({ id: _staff.id, name: _staff.name, avatar: _staff.avatar });
      const dateMoment = moment(strStartDate, 'MM-DD-YYYY hh:mm A');
      timePicker.current!.open(dateMoment, _staff.staffId, setStrStartDate);
    });
  };
  const handleChangeTime = () => {
    if (!timePicker.current) return;
    const dateMoment = moment(strStartDate, 'MM-DD-YYYY hh:mm A');
    timePicker.current.open(dateMoment, staff?.id, setStrStartDate);
  };

  const handleAddService = () => {
    if (!servicePicker.current) return;
    // Mapped shape diverges from widgets/ServicePicker's `Service` model (no `cateId`,
    // extra `staffId`) since this view repurposes the picker to also carry staff
    // assignment; cast rather than reshaping the runtime payload.
    servicePicker.current.open(services.map(o => ({
      duration: o.duration,
      id: o.id?.toString(),
      price: o.price,
      name: o.serviceName,
      staffId: o.staffId,
    })) as any, (sers: any[] = []) => {
      const temp: AppointmentServiceItem[] = [];
      sers.map(ser => {
        temp.push({
          duration: ser.duration,
          id: +ser.id,
          price: ser.price,
          serviceName: ser.name,
          uuid: uuid(),
          staffId: ser.staffId || staff?.id,
        });
      });
      setServices(temp);
    })
  };

  const handleChangeStaffService = (o: AppointmentServiceItem, staffId?: string) => () => {
    if (!staffPicker.current) return;
    dispatch(shopActions.data.staffAvailable.fetch({
      startTime: o.startTime,
      endTime: o.endTime,
    }));
    staffPicker.current.openWithStaffAvailable((_staff) => {
      const temp = [...services];
      // openWithStaffAvailable's callback type allows `_staff` to be null (picking
      // "Anyone"); this was already an unguarded `.id` access, so the latent
      // crash-on-Anyone risk is pre-existing and left as-is.
      temp.find(ser => ser.uuid === o.uuid)!.staffId = _staff!.id;
      setServices(temp);
    }, [staffId!]);
  }

  const handleRemoveService = (o: AppointmentServiceItem) => () => {
    const temp = [...services];
    setServices(temp.filter(ser => ser.uuid !== o.uuid));
  }

  const confirmApt = async () => {
    setLoading(true);
    try {
      let res = await API.put(`appointment/update-status`, { appointmentId: id, status: 1 });
      if (res.status === 200) {
        if (toastRef.current) toastRef.current.success('Updated!');
        goBack();
      }
    } catch (error: any) {
      if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'An error occurred, please try again!');
    } finally {
      setLoading(false);
    }
  };

  const handleProlongedTime = (status: string) => async () => {
    setLoading(true);
    try {
      let res = await API.post(`appointment/prolongedTime`, { payload: { appointmentId: id, status } });
      if (res.status === 200) {
        if (toastRef.current) toastRef.current.success('Updated!');
        await get_detail_appointment(true);
      }
    } catch (error: any) {
      if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'An error occurred, please try again!');
    } finally {
      setLoading(false);
    }
  };

  const deleteApt = async () => {
    if (!modalConfirm.current) return;
    modalConfirm.current.open({
      title: 'DELETE APPOINTMENT',
      message: 'You are about to delete the appointment?',
    }, async () => {
      setLoading(true);
      try {
        let res = await API.delete(`appointment/deleteAppointment/${id}`);
        if (res.status === 200) {
          goBack();
        }
      } catch (error: any) {
        if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'An error occurred, please try again!');
      } finally {
        setLoading(false);
      }
    })
  };

  const cancelApt = async () => {
    if (!reasonRef.current) return;
    reasonRef.current.open(async (reason) => {
      setLoading(true);
      try {
        let res = await API.put(`appointment/update-status`, { appointmentId: id, status: 4, reason });
        if (res.status === 200) {
          if (toastRef.current) toastRef.current.success('Updated!');
          goBack();
        }
      } catch (error: any) {
        if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'An error occurred, please try again!');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleUpdate = async () => {
    const objectStaff: Record<string, { serviceId: number; duration: number }[]> = {};
    services.map(o => {
      const item = {
        serviceId: +o.id!,
        duration: o.duration,
      };
      if (get(objectStaff, o.staffId as string)) {
        objectStaff[o.staffId as string].push(item);
      } else {
        set(objectStaff, [o.staffId as string], [item]);
      }
    });
    const staffs = Object.entries(objectStaff).map(([staffId, services]) => ({
      staffId: +staffId,
      serviceIds: services.map(o => o.serviceId),
      totalDuration: sumBy(services, o => o.duration) || 0,
    }));

    const startTimeTemp = moment(strStartDate, 'MM-DD-YYYY hh:mm A');
    const items = staffs.map(o => {
      return ({
        staffId: o.staffId,
        serviceIds: o.serviceIds,
        startTime: startTimeTemp?.format('MM/DD/YYYY HH:mm') || '',
        endTime: startTimeTemp?.add(o.totalDuration, 'minute').format('MM/DD/YYYY HH:mm') || '',
      });
    });

    const payload = {
      id,
      items,
      bookingType: 0,
      note,
      isAnybody: !staff?.id ? 1 : 0,
      // `handleUpdate` is only wired to the UpdateButton rendered after the
      // `!detail` early-return, so `detail` is non-null here at runtime.
      apiKey: detail!.shop!.apiKey,
    };

    setLoading(true);
    try {
      let res = await API.post(`appointment/updateAppointmentV3`, {
        payload,
      });
      if (res.status === 200) {
        if (toastRef.current) toastRef.current.success('Updated!');
        goBack();
      }
    } catch (error: any) {
      if (toastRef.current) toastRef.current.show(error?.response?.data?.message || 'An error occurred, please try again!');
    } finally {
      setLoading(false);
    }
  };

  const StatusBox = useMemo(() => {
    switch (detail?.status) {
      case AppointmentStatus.NEW:
        return <Button className="status-box" color="primary" ><span>NEW</span></Button>;
      case AppointmentStatus.CANCEL:
        return <Button className="status-box cancel"><span>CANCELED</span></Button>;
      case AppointmentStatus.DELETED:
        return <Button className="status-box cancel"><span>DELETED</span></Button>;
      case AppointmentStatus.CONFIRMED:
        return <Button className="status-box confirm" color="success"><span>CONFIRMED</span></Button>;
      case AppointmentStatus.FINISH:
        return <Button className="status-box confirm" color="primary" outline><span>FINISHED</span></Button>;
      default:
        return null;
    }
  }, [detail]);

  const StickFooter = () => {
    const disabledStaff = _history.location?.state?.disabled;
    const CancelButton = <Button onClick={cancelApt} type='button' className="btn btn-cancel" color='secondary'>Cancel</Button>;
    const CancelButtonDisabled = <Button disabled type='button' className="btn btn-cancel" color='secondary'>Canceled</Button>;
    const ConfirmButton = <Button onClick={confirmApt} type='button' className="btn btn-confirm" color="success" >Confirm</Button>;
    const DeleteButton = <Button onClick={deleteApt} type='button' className="btn btn-delete">Delete</Button>;
    const UpdateButton = <Button onClick={handleUpdate} type='button' className="btn btn-update" color="primary" >Update</Button>;
    const MinusButton = <Button disabled={detail!.duration! <= 15} onClick={handleProlongedTime('MINUS')} type='button' className="btn btn-cancel" color='secondary'>- 15 minutes</Button>;
    const AddButton = <Button onClick={handleProlongedTime('ADD')} type='button' className="btn btn-update" color="primary">+ 15 minutes</Button>;

    if (staffUsed) {
      // if (disabledStaff) return null;
      return <>
        <div className='sticky-footer-container add-minus-container' style={{ borderBottom: 'none' }}>
          {MinusButton} <span className='duration-label'>({detail!.duration}m)</span> {AddButton}
        </div>
        <div className='sticky-footer-container add-minus-container'>
          <Button onClick={() => setIsOpenAssign(true)} type='button' className="btn btn-cancel" color="secondary">Assign To</Button>
        </div>
      </>
    }

    switch (detail?.status) {
      case AppointmentStatus.CONFIRMED:
        return (
          <div className='sticky-footer-container'>
            {DeleteButton} {UpdateButton}
          </div>
        );
      case AppointmentStatus.NEW:
        return (
          <div className='sticky-footer-container'>
            {CancelButton} {ConfirmButton} {DeleteButton} {UpdateButton}
          </div>
        );
      case AppointmentStatus.CANCEL:
        return (
          <div className='sticky-footer-container'>
            {DeleteButton} {CancelButtonDisabled}
          </div>
        );
      case AppointmentStatus.FINISH:
      case AppointmentStatus.DELETED:
      case AppointmentStatus.CHECK_IN:
      default:
        return null;
    }
  };

  if (loadingPage) return <Loader />;
  if (!detail) return <Page title={'Not found'}>
    <p>404 Not Found</p>
  </Page>;

  return (
    <Page
      title={'Detail Appointment'}
      containerClass={'detail-appointment-wrapper'}
      StickFooter={StickFooter}
    >
      {loading && <Loader />}
      <div className="detail-appointment-content-container">
        <div className="status-container">
          {StatusBox}
        </div>
        <div className="detail-appointment-content-group">
          <div className="section-label"><span>Customer</span></div>
          <div className="customer-container">
            <div className="customer-name">{detail.customerName}</div>
            <div className='customer-phone'>{staffUsed ? maskPhone(detail.customerPhone) : formatPhone(detail.customerPhone)}</div>
          </div>
        </div>

        <div className="detail-appointment-content-group">
          <div className="section-label"><span>Booking Information</span></div>
          <div className="booking-information-container">
            <div className="information-row-content">
              <span className='label'>Staff</span>
              <div className="staff-container">
                <img className="staff-avatar" src={staff?.avatar || anyoneIcon} alt={'staff-avatar'} />
                <span className="value">{staff?.name || 'Anyone'}</span>
              </div>
              {!readOnly && <Button onClick={handleChangeStaff} className='btn-icon'>{iconEdit}</Button>}
            </div>

            <div className="line" />
            <div className="information-row-content">
              <span className='label'>Date</span>
              <span className='value'>{strStartDate || '--'}</span>
              {!readOnly && <Button onClick={handleChangeTime} className='btn-icon'>{iconEdit}</Button>}
            </div>
            <div className="line" />
            <div className="note-container">
              <div className="information-row-content">
                <span className='label'>{detail?.status === AppointmentStatus.CANCEL ? 'Reason' : 'Note'}</span>
                {readOnly && <span className='value'>{note || '--'}</span>}
              </div>
              {!readOnly && <Input type='textarea' value={note} onChange={e => setNote(e.target.value)} />}
            </div>
          </div>
        </div>

        <div className="detail-appointment-content-group" style={{ borderBottom: 'none' }}>
          <div className="service-top-action-container">
            <div className="section-label"><span>Services</span></div>
            {!readOnly && <div className="btn-actions">
              <Button onClick={handleAddService} className='btn-icon'><span style={{ color: '#0085E7', fontSize: 26, fontWeight: '300' }}>+</span></Button>
            </div>}
          </div>
          <div className="service-group-container">
            {services?.map((o) => {
              const staff = _staffList.find(s => s.id?.toString() === o.staffId?.toString());
              return (
                <div key={o.uuid} className='service-item-container'>
                  <div className='service-box'>
                    <div className="header-service">
                      <p className="name">{o.serviceName}</p>
                      {!readOnly && <Button onClick={handleRemoveService(o)} className='btn-icon'>{iconClose}</Button>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <p className="price">{formatCurrency(o.price)} ({o.duration}m)</p>
                      {!!staff && <button type='button' className="staff-input-picker-box" onClick={handleChangeStaffService(o, staff?.id)}>
                        <img src={staff?.avatar} alt={'A'} style={{ width: 24, height: 24 }} />
                        <span className='staff-name'>{staff?.name}</span>
                        <img src={downIcon} alt='downIcon' style={{ width: 24, height: 24 }} />
                      </button>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <StaffBSPicker ref={staffPicker} />
      <TimeSlotPicker ref={timePicker} staffId={staff?.id} />
      <ServicePicker ref={servicePicker} />
      <ModalReason ref={reasonRef} />
      <ModalConfirm ref={modalConfirm} />
      <AssignAppointment
        isOpenAssign={isOpenAssign}
        setIsOpenAssign={setIsOpenAssign}
        appointmentId={id}
        setLoading={setLoading}
      />
    </Page>
  )
}
export const DetailAppointmentForStaff = () => <DetailAppointmentV2 staffUsed />;
export default DetailAppointmentV2;

const iconEdit = <img src={editIcon} alt='editIcon' className={'icon-edit'} />
const iconClose = <img src={closeIcon} alt='closeIcon' className={'icon-close'} />